import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronRight, UserCheck, ShieldAlert, Laptop, DollarSign, Wallet } from 'lucide-react'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import SearchSelect from '@/components/common/SearchSelect'
import { API_URL } from '@/api/constant'
import { RoutesLink } from '@/utility/RouteLinks'
import { showToastNotification } from '@/utility/helper'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import OffboardingService from '@/services/Offboarding/OffboardingService'
import type { IUser } from '@/interface/employeeOnBoard'
import type { IOffboardingDetails, IOffboardingProcessStep } from '@/interface/offboarding.interface'

// Import Steps components
import ExitDetailsStep from './OffboardSteps/ExitDetailsStep'
import AssetClearanceStep from './OffboardSteps/AssetClearanceStep'
import AccountClearanceStep from './OffboardSteps/AccountClearanceStep'
import SettlementStep from './OffboardSteps/SettlementStep'
import Card from '@/components/common/Card'
import { Button } from '@/components/ui/button'
// import { Button } from '@/components/ui/button'

const STEPS = [
    { id: 'exit-details', title: 'Exit Details', icon: ShieldAlert },
    { id: 'assets', title: 'Asset Clearance', icon: Laptop },
    { id: 'accounts', title: 'Account Clearance', icon: DollarSign },
    { id: 'settlement', title: 'F&F Settlement', icon: Wallet }
]

const StartOffboarding: React.FC = () => {
    const { id, offboardingId } = useParams<{ id?: string; offboardingId?: string }>()
    const location = useLocation();
    const effectiveOffboardId = offboardingId || location?.state?.offboardingId;
    // const isCompleted = location?.state?.isCompleted || false;
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('exit-details')
    const [verifiedEmployees, setVerifiedEmployees] = useState<IUser[]>([])
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("")
    const [selectedEmployee, setSelectedEmployee] = useState<IUser | null>(null)
    const [initialExitData, setInitialExitData] = useState<IOffboardingDetails | null>(null)
    const [initialSystemAccessAvailableAt, setInitialSystemAccessAvailableAt] = useState<string>("")
    const [initialStatus, setInitialStatus] = useState<string>("InProgress")
    // Clearance Process checklist loaded from server
    const [clearanceSteps, setClearanceSteps] = useState<IOffboardingProcessStep[]>([
        { key: "asset-return", title: "Asset Return", status: "PENDING" },
        { key: "account-clearance", title: "Account Clearance", status: "PENDING" }
    ])
    const [isCompleted, setIsCompleted] = useState(false)
    const [isEmployeeSearchLoading, setIsEmployeeSearchLoading] = useState(false)

    // Load active verified employees list with server-side search
    const fetchEmployeesList = async (searchQuery: string = "") => {
        setIsEmployeeSearchLoading(true)
        try {
            const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ""
            const res = await EmployeeOnBoardService.getAllEmployees(`isVerified=1&pageSize=50&status=current${searchParam}`)
            if (res?.status) {
                setVerifiedEmployees(res.data?.user || [])
            }
        } catch (err) {
            console.error("Error loading verified employees:", err)
        } finally {
            setIsEmployeeSearchLoading(false)
        }
    }

    useEffect(() => {
        fetchEmployeesList()
    }, [])

    // If ID is loaded, fetch offboarding status details
    useEffect(() => {
        if (!id) {
            setSelectedEmployeeId("")
            setSelectedEmployee(null)
            setInitialExitData(null)
            setIsCompleted(false)
            return
        }

        const loadDetails = async () => {
            setIsLoading(true)
            try {
                // Get general employee details
                const empRes = await EmployeeOnBoardService.getEmployeeById(id)
                if (empRes?.status && empRes?.data) {
                    setSelectedEmployee(empRes.data)
                    setSelectedEmployeeId(empRes.data.id)
                }

                // Get offboarding specific progress details
                if (effectiveOffboardId) {
                    const offRes = await OffboardingService.getOffboardingDetails(effectiveOffboardId)
                    if (offRes?.status && offRes?.data) {
                        const data = offRes.data.offboarding
                        setInitialExitData({
                            lastWorkingDate: data.lastWorkingDate ? data.lastWorkingDate.split("T")[0] : "",
                            resignationDate: data.resignationDate ? data.resignationDate.split("T")[0] : "",
                            reason: data.reason || "Resignation",
                            noticePeriod: data.noticePeriod || "",
                            relievingDate: data.relievingDate ? data.relievingDate.split("T")[0] : "",
                            remarks: data.remarks || "",
                            handoverToUserId: data.handoverToUser?.id || "",
                            // handoverToUser: data.handoverToUser
                        })
                        setInitialSystemAccessAvailableAt(data.systemAccessAvailableAt ? data.systemAccessAvailableAt.split("T")[0] : "")
                        setInitialStatus(data.status || "InProgress")
                        if (data.offboardingProcess && data.offboardingProcess.length > 0) {
                            setClearanceSteps(data.offboardingProcess)
                        }
                        setIsCompleted(data.status === "Completed" ? true : false)
                    }
                }
            } catch (err) {
                console.error("Error loading offboarding details:", err)
                showToastNotification("danger", "No existing offboarding process found for this user.")
            } finally {
                setIsLoading(false)
            }
        }
        loadDetails()
    }, [id, effectiveOffboardId])


    // const handleStartOffboardClick = () => {
    //     if (!selectedEmployeeId) {
    //         showToastNotification("danger", "Please select an employee first")
    //         return
    //     }
    //     // Open Exit Details step input section
    //     setActiveTab('exit-details')
    // }

    const employeeOptions = useMemo(() => {
        const list = [...verifiedEmployees];
        if (selectedEmployee && !list.some(x => x.id === selectedEmployee.id)) {
            list.unshift(selectedEmployee);
        }
        return list.map(emp => ({
            value: emp.id,
            label: `${emp.name} (${emp.email}) | ${emp.designation?.name || 'Staff'} | ${emp.site?.name || 'N/A'}`,
            designation: emp.designation?.name,
            site: emp.site?.name,
            department: emp.department?.name
        }));
    }, [verifiedEmployees, selectedEmployee]);

    // console?.log("employeeOptions", employeeOptions)

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={id ? 'Edit Offboard' : 'Initiate Offboard'}
                    isBackButtonVisible
                />

                {/* Profile overview / Selection Card */}
                <Card>
                    <div className="space-y-6">
                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                            <UserCheck className="w-5 h-5 text-primary" />
                            Employee Exit Details
                        </h3>

                        {!id ? (
                            <div className="flex flex-col md:flex-row md:items-end gap-6">
                                <div className="flex-1">
                                    <SearchSelect
                                        label="Select Verified Employee"
                                        value={selectedEmployeeId}
                                        onChange={(val) => {
                                            setSelectedEmployeeId(val)
                                            if (!val) {
                                                setSelectedEmployee(null)
                                                return
                                            }
                                            const emp = verifiedEmployees.find(x => x.id === val) || (selectedEmployee?.id === val ? selectedEmployee : null)
                                            setSelectedEmployee(emp)
                                        }}
                                        options={employeeOptions}
                                        placeholder="Search and select verified employee..."
                                        onSearch={(term) => fetchEmployeesList(term)}
                                        isLoading={isEmployeeSearchLoading}
                                        debounceMs={400}
                                        allowClear
                                    />
                                </div>
                                {/* <Button
                                type="button"
                                onClick={handleStartOffboardClick}
                                disabled={!selectedEmployeeId}
                                className="h-[44px] rounded-xl px-6"
                            >
                                Start Offboarding
                            </Button> */}
                            </div>
                        ) : (
                            <div className="flex items-center gap-4 p-4 bg-slate-50/50 border border-slate-100 rounded-2xl">
                                {selectedEmployee?.profilePhoto ? (
                                    <img src={`${API_URL}/${selectedEmployee.profilePhoto}`} alt="" className="h-14 w-14 rounded-full object-cover border border-white shadow-sm" />
                                ) : (
                                    <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm">
                                        {selectedEmployee?.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                )}
                                <div>
                                    <h4 className="font-bold text-slate-900 text-lg">{selectedEmployee?.name}</h4>
                                    <p className="text-sm text-slate-500">{selectedEmployee?.email} • {selectedEmployee?.designation?.name || 'Staff'}</p>
                                    <p className="text-xs text-primary font-semibold mt-1">Site: {selectedEmployee?.site?.name || 'N/A'}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Steps renderer */}
                {selectedEmployee && (
                    <Card>
                        {/* Stepper Header tab bar */}
                        <div className="space-y-6 overflow-x-auto">
                            <div className="flex items-center min-w-[600px] gap-2">
                                {STEPS?.map((step, idx) => {
                                    // const StepIcon = step.icon
                                    const isActive = activeTab === step.id
                                    return (
                                        <React.Fragment key={step.id}>
                                            <Button
                                                disabled={!id && idx > 0}
                                                onClick={() => setActiveTab(step.id)}
                                                variant={isActive ? 'default' : 'secondary'}
                                            >
                                                {step.title}
                                            </Button>
                                            {idx < STEPS.length - 1 && (
                                                <ChevronRight className="text-slate-300 w-4 h-4 shrink-0" />
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </div>

                            {/* Current step card content */}
                            {activeTab === 'exit-details' && (
                                <ExitDetailsStep
                                    id={effectiveOffboardId}
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    userId={selectedEmployeeId}
                                    onNext={() => {
                                        // Automatically redirect to edit mode on first submission to assign details to id
                                        if (!id) {
                                            navigate(`${RoutesLink.offboardingList}`)
                                        }
                                        setActiveTab('assets')
                                    }}
                                    isCompleted={isCompleted}
                                    initialData={initialExitData}
                                />
                            )}

                            {activeTab === 'assets' && (
                                <AssetClearanceStep
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    userId={effectiveOffboardId}
                                    onNext={() => setActiveTab('accounts')}
                                    clearanceSteps={clearanceSteps}
                                    setClearanceSteps={setClearanceSteps}
                                    isCompleted={isCompleted}
                                />
                            )}

                            {activeTab === 'accounts' && (
                                <AccountClearanceStep
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    userId={effectiveOffboardId}
                                    onNext={() => setActiveTab('settlement')}
                                    clearanceSteps={clearanceSteps}
                                    setClearanceSteps={setClearanceSteps}
                                    isCompleted={isCompleted}
                                />
                            )}

                            {activeTab === 'settlement' && (
                                <SettlementStep
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    userId={effectiveOffboardId}
                                    noDuesUserId={id}
                                    clearanceSteps={clearanceSteps}
                                    isCompleted={isCompleted}
                                    setIsCompleted={setIsCompleted}
                                    initialSystemAccessAvailableAt={initialSystemAccessAvailableAt}
                                    initialStatus={initialStatus}
                                    initialData={initialExitData}

                                />
                            )}
                        </div>
                    </Card>
                )}
            </div>
        </Layout>
    )
}

export default StartOffboarding
